import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import FullScreenLoader from 'components/FullScreenLoader';
import AuthModal from 'components/AuthModal';
import { STATUS } from 'projectConstants';
import { resetDimension } from 'redux/DimensionPageSlice';
import { resetIndicator } from 'redux/IndicatorPageSlice';
import { resetItem } from 'redux/ItemPageSlice';
import { GetUserModels } from 'redux/ModelsSlice';
import { GetInputAdjustments, resetDriverAdjustmentSlider } from 'redux/driverAdjustmentSlice';
import {
  FetchBlockItemDimensions,
  ShowSidePanel,
  blockDetailStatus,
  blockDriversStatus,
  blockMetadataStatus,
  closeInputSection,
  fetchBlock,
  fetchBlockDrivers,
  fetchBlockMetadata,
  fetchModel,
  filterEndDate,
  filterStartDate,
  getAppliedFilters,
  getBlockDetails,
  getBlockDimensions,
  getBlockDriversData,
  getBlockMetadata,
  getChartFilter,
  getModelDetails,
  getPlanPageViewMode,
  getSelectedDriver,
  getTableFilter,
  groupByFilterChange,
  handleSidePanel,
  inputsPageShow,
  modelDetailStatus,
  overallGroupByFilter,
  overallIndicatorFilter,
  overallTimeFilter,
  planPageShow,
  reset,
  setDriver,
  setPlanPageViewMode,
  handleFilters,
  setSelectedBlockDimensionDirect,
  setSidePanelVisible,
  showAuthModel,
  toggleAuthModel,
} from 'redux/PlanPageSlice';
import {
  fetchAllScenarios,
  fetchScenarioStatus,
  getAllScenarios,
  getBaseScenario,
  getCompareScenario,
  resetScenario,
  selectBaseScenario,
  setCompareChartFilter,
  setCompareTableFilter,
  updateModelandBlock,
} from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import store from 'redux/store/store';
import { useBloxBlockPageTitle } from 'hooks/useBloxDocumentTitle';
import { AppDispatch, getClosestToTargetItemCountDimension } from 'utils/GlobalHelpers';

import {
  refetchPlanPageAfterInputPanelClose,
  resetPlanPageRefetchScheduler,
  schedulePlanPageRefetch,
} from '../planPageFetchHelpers';
import {
  readSavedPlanBlockFilters,
  mergeSavedFiltersWithBlockDimensions,
} from '../planPageFilterHydration';
import {
  selectHasCachedCompareData,
  selectHasCachedPlanData,
  selectIsCompareMode,
  selectIsCompareRefetching,
  selectIsPlanRefetching,
} from '../planPageRevampSelectors';
import {
  PLAN_CHART_CARD_RADIUS,
  PLAN_CHART_SPLIT_VIEW_HEIGHT,
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_MAIN_CONTENT_GAP,
  PLAN_REVAMP_RADIUS,
} from '../planPageRevampUi';
import { usePlanPageUiRevamp } from '../usePlanPageUiRevamp';

import PlanChartPanel from './PlanChartPanel';
import PlanCompareChartPanel from './PlanCompareChartPanel';
import PlanCompareDataTable from './PlanCompareDataTable';
import PlanPageTitleBar from './PlanPageTitleBar';
import KpiSummaryBar from './KpiSummaryBar';
import PlanToolbar, { PlanViewMode } from './PlanToolbar';
import InputsPanel from './InputsPanel';
import DriverInputPanel from './DriverInputPanel';
import PlanDataTable from './PlanDataTable';
import PlanWhatIfsPanel from './PlanWhatIfsPanel';
import PlanSoftLoadingOverlay from './PlanSoftLoadingOverlay';

interface PlanAnalyticsCardProperties {
  children: ReactNode;
  borderColor: string;
  surfaceColor: string;
  flex?: string;
  minH?: number | string;
  h?: number | string;
  overflow?: 'hidden' | 'auto' | 'visible';
  borderRadius?: string;
}

const PlanAnalyticsCard = memo(
  ({
    children,
    borderColor,
    surfaceColor,
    flex = '1 1 0',
    minH = 0,
    h,
    overflow = 'hidden',
    borderRadius = PLAN_REVAMP_RADIUS,
  }: PlanAnalyticsCardProperties) => (
    <Flex
      direction='column'
      flex={flex}
      minW={0}
      minH={minH}
      h={h}
      bg={surfaceColor}
      border='1px solid'
      borderColor={borderColor}
      borderRadius={borderRadius}
      overflow={overflow}
      position='relative'
    >
      {children}
    </Flex>
  ),
);

PlanAnalyticsCard.displayName = 'PlanAnalyticsCard';

interface PlanChartSectionProperties {
  isCompareMode: boolean;
  viewMode: PlanViewMode;
  surfaceColor: string;
  onToggleAuthModal: () => void;
}

const PlanChartSection = memo(
  ({ isCompareMode, viewMode, surfaceColor, onToggleAuthModal }: PlanChartSectionProperties) => {
    const isChartOnly = viewMode === 'chart';
    const isSplitView = viewMode === 'chart-table';

    return (
      <Box
        flex={isChartOnly || isSplitView ? '1 1 0' : '0 0 auto'}
        flexShrink={isChartOnly || isSplitView ? undefined : 0}
        h={isChartOnly || isSplitView ? '100%' : 'auto'}
        minH={isChartOnly || isSplitView ? 0 : undefined}
        minW={0}
        display='flex'
        flexDirection='column'
        overflow={isSplitView ? 'hidden' : 'visible'}
        bg={surfaceColor}
      >
        {isCompareMode ? (
          <PlanCompareChartPanel
            key={viewMode}
            fillHeight={isChartOnly || isSplitView}
            onToggleAuthModal={onToggleAuthModal}
          />
        ) : (
          <PlanChartPanel
            key={viewMode}
            fillHeight={isChartOnly || isSplitView}
            onToggleAuthModal={onToggleAuthModal}
          />
        )}
      </Box>
    );
  },
);

PlanChartSection.displayName = 'PlanChartSection';

interface PlanTableSectionProperties {
  isCompareMode: boolean;
  onToggleAuthModal: () => void;
  pageScrollMode?: boolean;
}

const PlanTableSection = memo(
  ({ isCompareMode, onToggleAuthModal, pageScrollMode = false }: PlanTableSectionProperties) => (
    <Box
      flex={pageScrollMode ? '1 1 auto' : '1 1 0'}
      minH={pageScrollMode ? undefined : 0}
      minW={0}
      overflow={pageScrollMode ? 'visible' : 'hidden'}
      display='flex'
      flexDirection='column'
      h={pageScrollMode ? 'auto' : undefined}
    >
      {isCompareMode ? (
        <PlanCompareDataTable
          onToggleAuthModal={onToggleAuthModal}
          pageScrollMode={pageScrollMode}
        />
      ) : (
        <PlanDataTable onToggleAuthModal={onToggleAuthModal} pageScrollMode={pageScrollMode} />
      )}
    </Box>
  ),
);

PlanTableSection.displayName = 'PlanTableSection';

const PlanPageContent = () => {
  const isRevampEnabled = usePlanPageUiRevamp();
  const { pathname } = useLocation();
  const { modelId, blockId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const initializedBlockReference = useRef<string | null>(null);
  const shouldForceRefetchAfterHydration = useRef(false);
  const [filtersHydrated, setFiltersHydrated] = useState(false);

  const scenarios = useSelector(getAllScenarios);
  const baseScenario = useSelector(getBaseScenario);
  const compareScenario = useSelector(getCompareScenario);
  const metadata = useSelector(getBlockMetadata);
  const blockDetail = useSelector(getBlockDetails);
  const blockDimensions = useSelector(getBlockDimensions);
  const modelDetail = useSelector(getModelDetails);
  const selectedGroupBy = useSelector(overallGroupByFilter);
  const selectedYear = useSelector(overallTimeFilter);
  const appliedFilters = useSelector(getAppliedFilters);
  const planChartFilter = useSelector(getChartFilter);
  const planTableFilter = useSelector(getTableFilter);
  const indicatorFilter = useSelector(overallIndicatorFilter);
  const inputAdjustments = useSelector(GetInputAdjustments);
  const startDatePlanPage = useSelector(filterStartDate);
  const endDatePlanPage = useSelector(filterEndDate);
  const modelStatus = useSelector(modelDetailStatus);
  const blockStatus = useSelector(blockDetailStatus);
  const driversStatus = useSelector(blockDriversStatus);
  const metadataStatus = useSelector(blockMetadataStatus);
  const scenarioStatus = useSelector(fetchScenarioStatus);

  const showInputsPanel = useSelector(ShowSidePanel);
  const showInputPage = useSelector(inputsPageShow);
  const selectedDriver = useSelector(getSelectedDriver);
  const showPlanPage = useSelector(planPageShow);
  const viewMode = useSelector(getPlanPageViewMode) as PlanViewMode;
  const themeColors = useSelector(getPlanPageThemeColors);
  const isAuthModalOpen = useSelector(showAuthModel);
  const isCompareMode = useSelector(selectIsCompareMode);
  const isPlanRefetching = useSelector(selectIsPlanRefetching);
  const isCompareRefetching = useSelector(selectIsCompareRefetching);
  const hasCachedPlanData = useSelector(selectHasCachedPlanData);
  const hasCachedCompareData = useSelector(selectHasCachedCompareData);
  const blockDrivers = useSelector(getBlockDriversData);
  const [showWhatIfsPanel, setShowWhatIfsPanel] = useState(false);

  useBloxBlockPageTitle(blockDetail?.name, modelDetail?.name, 'Plan');

  const hasWhatIfsDrivers = useMemo(
    () => (blockDrivers ?? []).some((driver: { type?: string }) => driver.type === 'indicator'),
    [blockDrivers],
  );

  const onToggleAuthModal = useCallback(() => {
    dispatch(toggleAuthModel());
  }, [dispatch]);

  const onViewModeChange = useCallback(
    (mode: PlanViewMode) => {
      dispatch(setPlanPageViewMode(mode));
    },
    [dispatch],
  );

  const onToggleWhatIfsPanel = useCallback(() => {
    setShowWhatIfsPanel((isOpen) => !isOpen);
  }, []);

  const onCloseWhatIfsPanel = useCallback(() => {
    setShowWhatIfsPanel(false);
  }, []);

  const dateFilterSelected = Boolean(startDatePlanPage && endDatePlanPage);
  const dateFilter = `[{ 'start_date': '${startDatePlanPage}', 'end_date': '${endDatePlanPage}' }]`;

  const blockDimensionsReady =
    blockDimensions != null &&
    !Array.isArray(blockDimensions) &&
    Array.isArray(blockDimensions.dimensions);

  const isLoading =
    modelStatus === STATUS.LOADING ||
    blockStatus === STATUS.LOADING ||
    driversStatus === STATUS.LOADING ||
    metadataStatus === STATUS.LOADING ||
    scenarioStatus === STATUS.LOADING;

  const canFetchData =
    Boolean(blockId && baseScenario?.id && metadata && selectedGroupBy !== null && selectedYear) &&
    !isLoading;

  const showsTable = showPlanPage && (viewMode === 'table' || viewMode === 'chart-table');
  const showsChart = showPlanPage && (viewMode === 'chart' || viewMode === 'chart-table');
  const isSplitAnalytics = viewMode === 'chart-table' && showsChart && showsTable;
  const analyticsScrollReference = useRef<HTMLDivElement>(null);
  const [splitViewportHeight, setSplitViewportHeight] = useState(0);

  const splitChartHeight = useMemo(() => {
    if (splitViewportHeight <= 0) {
      return PLAN_CHART_SPLIT_VIEW_HEIGHT;
    }

    return `${Math.round(splitViewportHeight * 0.7)}px`;
  }, [splitViewportHeight]);

  const splitTableMinHeight =
    splitViewportHeight > 0 ? `${Math.round(splitViewportHeight * 0.3)}px` : null;

  useLayoutEffect(() => {
    if (!isSplitAnalytics) {
      setSplitViewportHeight(0);
      return () => {};
    }

    const element = analyticsScrollReference.current;
    if (!element) {
      return () => {};
    }

    const updateViewportHeight = () => {
      setSplitViewportHeight(element.clientHeight);
    };

    updateViewportHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateViewportHeight);
      return () => window.removeEventListener('resize', updateViewportHeight);
    }

    const resizeObserver = new ResizeObserver(updateViewportHeight);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [isSplitAnalytics, showWhatIfsPanel, showInputsPanel, viewMode]);

  const showSoftOverlay = showPlanPage
    ? isCompareMode
      ? isCompareRefetching && (hasCachedCompareData || inputAdjustments !== null)
      : isPlanRefetching && (hasCachedPlanData || inputAdjustments !== null)
    : false;

  useEffect(() => {
    if (!showPlanPage) {
      setShowWhatIfsPanel(false);
    }
  }, [showPlanPage]);

  useEffect(() => {
    dispatch(GetUserModels({}));
  }, [dispatch]);

  useEffect(() => {
    dispatch(reset());
    dispatch(resetScenario());
    dispatch(resetDriverAdjustmentSlider());
    dispatch(setDriver(null));
    dispatch(resetItem());
    dispatch(resetDimension());
    dispatch(resetIndicator());
    setShowWhatIfsPanel(false);
    initializedBlockReference.current = null;
  }, [pathname, dispatch]);

  useEffect(() => {
    if (!blockId || initializedBlockReference.current === blockId) return;
    initializedBlockReference.current = blockId;
    dispatch(setSidePanelVisible(false));
    dispatch(setPlanPageViewMode('chart-table'));
  }, [blockId, dispatch]);

  useEffect(() => {
    if (modelId && blockId) {
      dispatch(updateModelandBlock({ modelId, blockId }));
      dispatch(fetchModel(Number(modelId)));
      dispatch(fetchBlock(Number(blockId)));
      dispatch(fetchAllScenarios(Number(modelId)));
    }
    return () => {
      dispatch(reset());
      dispatch(resetScenario());
      dispatch(resetDriverAdjustmentSlider());
      dispatch(setDriver(null));
      dispatch(resetItem());
      dispatch(resetDimension());
      dispatch(resetIndicator());
    };
  }, [modelId, blockId, dispatch]);

  useEffect(() => {
    if (!modelId || scenarios.length === 0) return;

    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    const modalFilters = data[modelId] || {};

    if (modalFilters?.scenario) {
      const scenarioToSelect =
        typeof modalFilters.scenario === 'object' && modalFilters.scenario.id
          ? scenarios.find((scenario: any) => scenario.id === modalFilters.scenario.id)
          : scenarios.find((scenario: any) => scenario.id === modalFilters.scenario);
      if (scenarioToSelect) {
        dispatch(selectBaseScenario(scenarioToSelect));
        return;
      }
    }

    if (!baseScenario) {
      const defaultScenario = scenarios.find((scenario: any) => scenario.is_base) ?? scenarios[0];
      if (defaultScenario) dispatch(selectBaseScenario(defaultScenario));
    }
  }, [scenarios, baseScenario, modelId, dispatch]);

  useEffect(() => {
    if (metadata && blockId && modelId && selectedGroupBy === null) {
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[modelId] || {};
      const blockGroupBy = modalFilters?.[blockId]?.groupBy
        ? metadata.dimensions?.find((dim: any) => dim.id === modalFilters[blockId].groupBy)
        : null;
      const defaultGroupBy = blockGroupBy
        ? blockGroupBy.id
        : metadata.dimensions && metadata.dimensions.length > 0
        ? getClosestToTargetItemCountDimension(metadata.dimensions)
        : null;
      dispatch(groupByFilterChange(defaultGroupBy || 'Total'));
    }
  }, [metadata, selectedGroupBy, blockId, modelId, dispatch]);

  useLayoutEffect(() => {
    resetPlanPageRefetchScheduler();
    shouldForceRefetchAfterHydration.current = false;

    if (!blockId || !modelId) {
      setFiltersHydrated(false);
      return;
    }

    const savedFilters = readSavedPlanBlockFilters(blockDetail?.model_id ?? modelId, blockId);
    if (savedFilters?.selectedBlockDimension?.length) {
      dispatch(setSelectedBlockDimensionDirect(savedFilters.selectedBlockDimension));
      dispatch(handleFilters());
      shouldForceRefetchAfterHydration.current = true;
    }

    setFiltersHydrated(true);
  }, [blockId, dispatch, modelId]);

  useEffect(() => {
    if (!blockId || !modelId || !blockDimensionsReady) return;

    const savedFilters = readSavedPlanBlockFilters(blockDetail?.model_id ?? modelId, blockId);
    if (!savedFilters?.selectedBlockDimension?.length) return;

    const mergedFilters = mergeSavedFiltersWithBlockDimensions(
      savedFilters.selectedBlockDimension,
      blockDimensions.dimensions,
    );
    dispatch(setSelectedBlockDimensionDirect(mergedFilters));
    dispatch(handleFilters());
  }, [blockDimensions.dimensions, blockDimensionsReady, blockId, dispatch, modelId]);

  useEffect(() => {
    if (blockId && baseScenario?.id) {
      dispatch(fetchBlockDrivers({ blockId, parameters: { scenario_id: baseScenario.id } }));
      dispatch(
        fetchBlockMetadata({
          blockId: Number(blockId),
          parameters: { scenario_id: baseScenario.id },
        }),
      );
      dispatch(
        FetchBlockItemDimensions({
          blockId,
          parameters: { params: { scenario_id: baseScenario.id } },
        }),
      );
    }
  }, [blockId, baseScenario?.id, dispatch]);

  useEffect(() => {
    if (!compareScenario?.id) return;
    dispatch(setCompareChartFilter(planChartFilter || 'M'));
    dispatch(setCompareTableFilter(planTableFilter || 'M'));
  }, [compareScenario?.id, planChartFilter, planTableFilter, dispatch]);

  const refetchRequest = useCallback(
    (options?: { force?: boolean }) => {
      if (!canFetchData || !filtersHydrated) return;
      schedulePlanPageRefetch(dispatch, store.getState, blockId, options, modelId);
    },
    [blockId, canFetchData, dispatch, filtersHydrated, modelId],
  );

  const onToggleInputsPanel = useCallback(() => {
    if (showInputsPanel && showInputPage) {
      refetchPlanPageAfterInputPanelClose(refetchRequest, selectedDriver);
      dispatch(closeInputSection());
    }
    dispatch(handleSidePanel());
  }, [dispatch, refetchRequest, selectedDriver, showInputPage, showInputsPanel]);

  const onCollapseInputsPanel = useCallback(() => {
    refetchPlanPageAfterInputPanelClose(refetchRequest, selectedDriver);
    dispatch(closeInputSection());
    dispatch(setSidePanelVisible(false));
  }, [dispatch, refetchRequest, selectedDriver]);

  useEffect(() => {
    if (!canFetchData || !filtersHydrated) return;
    if (!showPlanPage && !isCompareMode) return;

    const force = shouldForceRefetchAfterHydration.current;
    shouldForceRefetchAfterHydration.current = false;
    refetchRequest(force ? { force: true } : undefined);
  }, [
    appliedFilters,
    baseScenario?.id,
    blockId,
    compareScenario?.id,
    dateFilter,
    dateFilterSelected,
    filtersHydrated,
    indicatorFilter,
    inputAdjustments,
    selectedGroupBy,
    selectedYear,
    canFetchData,
    showPlanPage,
    isCompareMode,
    refetchRequest,
  ]);

  if (isLoading) return <FullScreenLoader />;

  if (!isRevampEnabled) {
    return null;
  }

  return (
    <Flex direction='column' flex='1' minH={0} bg={themeColors.background}>
      <Box
        flexShrink={0}
        bg={themeColors.surface}
        borderBottom='1px solid'
        borderColor={themeColors.border}
      >
        <PlanPageTitleBar refetchRequest={refetchRequest} />
        <KpiSummaryBar />
        <PlanToolbar
          showInputsPanel={showInputsPanel}
          onToggleInputsPanel={onToggleInputsPanel}
          showWhatIfsPanel={showWhatIfsPanel}
          onToggleWhatIfsPanel={onToggleWhatIfsPanel}
          hasWhatIfsDrivers={hasWhatIfsDrivers}
          showAnalytics={showPlanPage}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          refetchRequest={refetchRequest}
        />
      </Box>

      <Flex flex='1' minH={0} minW={0} overflow='hidden' bg={themeColors.background}>
        {showInputsPanel && (
          <InputsPanel onCollapse={onCollapseInputsPanel} refetchRequest={refetchRequest} />
        )}
        {showInputPage && (
          <Box
            flex={showPlanPage ? undefined : '1'}
            minW={showPlanPage ? '360px' : 0}
            minH={0}
            display='flex'
            flexDirection='column'
            overflow='hidden'
          >
            <DriverInputPanel refetchRequest={refetchRequest} />
          </Box>
        )}

        {showPlanPage && (
          <Box
            flex='1 1 0'
            minW={0}
            minH={0}
            overflow='hidden'
            paddingRight={
              showWhatIfsPanel && hasWhatIfsDrivers ? 4 : PLAN_HEADER_HORIZONTAL_PADDING
            }
            paddingLeft={showInputsPanel ? 4 : PLAN_HEADER_HORIZONTAL_PADDING}
            pt={3}
            pb={4}
            display='flex'
            gap={PLAN_MAIN_CONTENT_GAP}
          >
            <Box
              ref={analyticsScrollReference}
              flex='1 1 0'
              minW={0}
              minH={0}
              overflowY='auto'
              overflowX='hidden'
              className='scroll'
              position='relative'
            >
              <PlanSoftLoadingOverlay isVisible={showSoftOverlay} color={themeColors.primary} />
              {isSplitAnalytics ? (
                <Flex direction='column' gap={PLAN_MAIN_CONTENT_GAP}>
                  <PlanAnalyticsCard
                    flex='0 0 auto'
                    h={splitChartHeight}
                    minH={splitChartHeight}
                    borderColor={themeColors.border}
                    surfaceColor={themeColors.surface}
                    borderRadius={PLAN_CHART_CARD_RADIUS}
                    overflow='hidden'
                  >
                    <PlanChartSection
                      isCompareMode={isCompareMode}
                      viewMode={viewMode}
                      surfaceColor={themeColors.surface}
                      onToggleAuthModal={onToggleAuthModal}
                    />
                  </PlanAnalyticsCard>
                  <PlanAnalyticsCard
                    flex='0 0 auto'
                    {...(splitTableMinHeight ? { minH: splitTableMinHeight } : {})}
                    borderColor={themeColors.border}
                    surfaceColor={themeColors.surface}
                    overflow='visible'
                  >
                    <PlanTableSection
                      isCompareMode={isCompareMode}
                      onToggleAuthModal={onToggleAuthModal}
                      pageScrollMode
                    />
                  </PlanAnalyticsCard>
                </Flex>
              ) : (
                <PlanAnalyticsCard
                  flex='1 1 0'
                  minH={0}
                  h='100%'
                  borderColor={themeColors.border}
                  surfaceColor={themeColors.surface}
                  overflow='hidden'
                >
                  {showsChart && (
                    <PlanChartSection
                      isCompareMode={isCompareMode}
                      viewMode={viewMode}
                      surfaceColor={themeColors.surface}
                      onToggleAuthModal={onToggleAuthModal}
                    />
                  )}
                  {showsTable && (
                    <PlanTableSection
                      isCompareMode={isCompareMode}
                      onToggleAuthModal={onToggleAuthModal}
                    />
                  )}
                </PlanAnalyticsCard>
              )}
            </Box>
            {showWhatIfsPanel && hasWhatIfsDrivers && (
              <PlanWhatIfsPanel
                onToggleAuthModal={onToggleAuthModal}
                onClose={onCloseWhatIfsPanel}
              />
            )}
          </Box>
        )}
      </Flex>
      <AuthModal isOpen={isAuthModalOpen} onClose={onToggleAuthModal} />
    </Flex>
  );
};

export default memo(PlanPageContent);
